import type { ReactNode } from 'react';
import { Fragment, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { AppSidebar } from '@/components/layout/sidebar/AppSidebar';
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { AIChatSidePanel } from '@/features/ai-chat/AIChatSidePanel';
import { useAIChatStore } from '@/store/useAIChatStore';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { useRouterState } from '@tanstack/react-router';
import { APP_ROUTES } from '@/const/routes';
import { useActiveCompanyId } from '@/queries/dashboard.queries';

interface IProps {
  children: ReactNode;
  headerText?: ReactNode;
  headerRightContent?: ReactNode;
  hideSideBar?: boolean;
  hideHeader?: boolean;
  subHeader?: ReactNode;
  contentClassName?: string;
  mainTagClassName?: string;
  customSideBar?: ReactNode;
}

const CONTENT_CSS = 'max-w-[1600px] w-full mx-auto';

const LayoutContent = ({
  children,
  headerText,
  headerRightContent,
  hideSideBar,
  hideHeader,
  subHeader,
  contentClassName,
  mainTagClassName,
  customSideBar,
}: IProps) => {
  const { state } = useSidebar();
  const { setIsPanelOpen, isPanelOpen, saveCompanySession, restoreCompanySession } = useAIChatStore();
  const { location } = useRouterState();
  const isAIChatPage = location.pathname === APP_ROUTES.app.aiChat;

  const companyId = useActiveCompanyId();
  const prevCompanyIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (companyId === undefined) return;
    if (prevCompanyIdRef.current !== undefined && prevCompanyIdRef.current !== companyId) {
      saveCompanySession(prevCompanyIdRef.current);
      restoreCompanySession(companyId);
    }
    prevCompanyIdRef.current = companyId;
  }, [companyId, saveCompanySession, restoreCompanySession]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Fragment>
      {customSideBar ? customSideBar : <AppSidebar isHidden={hideSideBar} />}

      <main
        id="general-layout-content"
        className={cn(
          'flex flex-col grow min-w-0 overflow-hidden mx-auto px-4 lg:px-8 xl:px-10 pb-2 mb-2 pt-[67px]',
          'group',
          'bg-surface',
          state === 'collapsed' && 'collapsed',
          mainTagClassName,
        )}
      >
        {!hideHeader && (
          <header
            className={cn(
              'fixed top-0 right-0 z-40 backdrop-blur-md',
              'flex gap-4 items-center justify-between px-4 lg:px-8 xl:px-10 min-h-[67px] transition-all duration-200 ease-linear',
              'left-64 group-[.collapsed]:left-12',
            )}
          >
            <div className={cn('flex gap-4 items-center justify-between w-full', CONTENT_CSS)}>
              <h1>{headerText}</h1>
              <div className="flex items-center gap-2">
                {headerRightContent}
                {!isAIChatPage && (
                  <Button
                    variant={isPanelOpen ? 'default' : 'outline'}
                    size="sm"
                    className="gap-1.5 text-xs h-8"
                    onClick={() => setIsPanelOpen(!isPanelOpen)}
                  >
                    <Sparkles className="size-3.5" />
                    AI
                  </Button>
                )}
              </div>
            </div>
          </header>
        )}

        {subHeader && (
          <div className={cn('w-full mt-4', CONTENT_CSS)}>{subHeader}</div>
        )}

        <div className={cn('w-full grow flex gap-6')}>
          <div
            id="general-layout-main"
            className={cn('@container w-full grow mt-5', CONTENT_CSS, contentClassName)}
          >
            {children}
          </div>
        </div>
      </main>

      <AIChatSidePanel />
    </Fragment>
  );
};

export const GeneralLayout = (props: IProps) => {
  return (
    <Fragment>
      <div className="font-outfit mx-auto w-full h-screen items-start">
        <SidebarProvider>
          <LayoutContent {...props}>{props.children}</LayoutContent>
        </SidebarProvider>
      </div>
    </Fragment>
  );
};
